import { isRTL } from "@core/shared/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@design-system/ui/alert-dialog";
import { trans } from "@mongez/localization";
import { portalAtom } from "@mongez/react-atom";
import { type ReactNode } from "react";

type ToastConfirmOptions = {
  heading: ReactNode;
  content: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmButton?: ReactNode;
  cancelButton?: ReactNode;
  confirmColor?: keyof typeof confirmColors;
};

export async function toastConfirm(
  options: ToastConfirmOptions,
): Promise<boolean>;
export async function toastConfirm(
  heading: ReactNode,
  content: ReactNode,
): Promise<boolean>;
export async function toastConfirm(
  optionsOrHeading: ToastConfirmOptions | ReactNode,
  content?: ReactNode,
): Promise<boolean> {
  return new Promise((resolve) => {
    if (
      typeof optionsOrHeading === "object" &&
      optionsOrHeading !== null &&
      "heading" in optionsOrHeading
    ) {
      const options = optionsOrHeading as ToastConfirmOptions;
      toastConfirmPortal.open({
        ...options,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    } else {
      toastConfirmPortal.open({
        heading: optionsOrHeading,
        content,
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    }
  });
}

const toastConfirmPortal = portalAtom("toastConfirm");

const confirmColors = {
  red: "bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white",
  green: "bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700 text-white",
  blue: "bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white",
  yellow: "bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-white",
  purple: "bg-purple-600 hover:bg-purple-700 dark:bg-purple-600 dark:hover:bg-purple-700 text-white",
  pink: "bg-pink-600 hover:bg-pink-700 dark:bg-pink-600 dark:hover:bg-pink-700 text-white",
  dark: "bg-gray-900 hover:bg-gray-950 dark:bg-gray-800 dark:hover:bg-gray-700 text-white",
  primary: "bg-primary hover:bg-primary/90 dark:bg-primary dark:hover:bg-primary/80 text-white dark:text-gray-900 shadow-lg dark:shadow-xl",
} as const;

export function ToastConfirm() {
  const {
    heading,
    content,
    onConfirm,
    onCancel,
    confirmLabel = trans("confirm"),
    cancelLabel = trans("cancel"),
    confirmButton,
    cancelButton,
    confirmColor = "primary",
  } = toastConfirmPortal.useData();
  const opened = toastConfirmPortal.useOpened();

  const confirmButtonColor = confirmColors[confirmColor];

  // Detect RTL
  const isRTLDirection = isRTL();

  return (
    <>
      <AlertDialog
        open={opened}
        onOpenChange={(opened) => toastConfirmPortal.change("opened", opened)}
      >
        <AlertDialogContent className="mx-auto w-[95%] max-w-md p-4 pb-5 sm:w-[90%] sm:max-w-lg md:w-[80%] md:max-w-xl lg:w-1/3 xl:w-1/4 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-xl dark:shadow-2xl transition-colors duration-300">
          <AlertDialogHeader>
            <AlertDialogTitle
              className={`text-lg sm:text-xl text-gray-900 dark:text-gray-100 ${isRTLDirection ? "text-right" : ""}`}
            >
              {heading}
            </AlertDialogTitle>
            <AlertDialogDescription
              className={`text-sm sm:text-base text-gray-600 dark:text-gray-300 ${isRTLDirection ? "text-right" : ""}`}
            >
              {content}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter
            className={
              isRTLDirection
                ? "flex flex-col-reverse gap-2 sm:flex-row-reverse sm:gap-3"
                : "flex flex-col-reverse gap-2 sm:flex-row sm:gap-3"
            }
          >
            {isRTLDirection ? (
              <>
                {confirmButton ?? (
                  <AlertDialogAction
                    className={`w-full sm:w-auto ${confirmButtonColor}`}
                    onClick={onConfirm}
                  >
                    {confirmLabel}
                  </AlertDialogAction>
                )}
                {cancelButton ?? (
                  <AlertDialogCancel
                    className="w-full sm:w-auto bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-300"
                    onClick={onCancel}
                  >
                    {cancelLabel}
                  </AlertDialogCancel>
                )}
              </>
            ) : (
              <>
                {cancelButton ?? (
                  <AlertDialogCancel
                    className="w-full sm:w-auto bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-300"
                    onClick={onCancel}
                  >
                    {cancelLabel}
                  </AlertDialogCancel>
                )}
                {confirmButton ?? (
                  <AlertDialogAction
                    className={`w-full sm:w-auto ${confirmButtonColor}`}
                    onClick={onConfirm}
                  >
                    {confirmLabel}
                  </AlertDialogAction>
                )}
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
